import { User } from '../../types'

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface Props {
  user: Pick<User, 'avatar_display' | 'avatar_url' | 'full_name'> & { avatar_size?: AvatarSize }
  size?: AvatarSize
  className?: string
  variant?: 'default' | 'primary'
  online?: boolean
  dimmed?: boolean
}

const SIZE_CLASSES: Record<AvatarSize, { container: string; text: string; img: string; dot: string }> = {
  xs: { container: 'w-6 h-6', text: 'text-[10px]', img: 'w-6 h-6', dot: 'w-2 h-2 -bottom-0.5 -right-0.5' },
  sm: { container: 'w-8 h-8', text: 'text-xs', img: 'w-8 h-8', dot: 'w-2.5 h-2.5 -bottom-0.5 -right-0.5' },
  md: { container: 'w-10 h-10', text: 'text-sm', img: 'w-10 h-10', dot: 'w-3 h-3 -bottom-0.5 -right-0.5' },
  lg: { container: 'w-12 h-12', text: 'text-base', img: 'w-12 h-12', dot: 'w-3 h-3 -bottom-0.5 -right-0.5' },
  xl: { container: 'w-16 h-16', text: 'text-lg', img: 'w-16 h-16', dot: 'w-3.5 h-3.5 -bottom-0.5 -right-0.5' },
}

const VARIANT_CLASSES = {
  default: 'bg-[#351E1D]/10 text-text-primary',
  primary: 'bg-pinnacle-red text-white',
}

export default function UserAvatar({ user, size, className = '', variant = 'default', online, dimmed }: Props) {
  const effectiveSize = size || user.avatar_size || 'md'
  const sizeConfig = SIZE_CLASSES[effectiveSize]
  const variantClass = VARIANT_CLASSES[variant]
  const dimClass = dimmed ? 'opacity-40' : ''

  const dot = online ? (
    <span className={`absolute ${sizeConfig.dot} bg-[#15532E] rounded-full border-2 border-white`} />
  ) : null

  if (user.avatar_url) {
    return (
      <div className={`relative inline-flex flex-shrink-0 ${className}`}>
        <img
          src={user.avatar_url}
          alt={user.full_name}
          title={user.full_name}
          className={`${sizeConfig.img} rounded-full object-cover flex-shrink-0 ${dimClass}`}
        />
        {dot}
      </div>
    )
  }

  return (
    <div className={`relative inline-flex flex-shrink-0 ${className}`}>
      <div
        title={user.full_name}
        className={`${sizeConfig.container} ${sizeConfig.text} ${variantClass} rounded-full flex items-center justify-center font-semibold select-none ${dimClass}`}
      >
        {user.avatar_display}
      </div>
      {dot}
    </div>
  )
}
